<template>
  <component
    :is="props._index !== 0 ? Transition : 'div'"
    name="fade"
    :appear="props._index !== 0 ? true : undefined"
  >
    <component
      :is="props._index !== 0 ? IntersectionObserver : 'div'"
      :options="computedIntersectionOptions"
      @intersect="onIntersect"
    >
      <div
        v-if="props.data"
        ref="componentRef"
        class="dynamic-article"
        :class="{
          'is--not-first': props._index !== 0,
          'is--hidden': props._index !== 0 && !intersected,
        }"
      >
        <HeroArticle
          v-bind="props.data"
          :show-meta="state.showMeta"
          :show-share-button="state.showShare"
          :variant="state.heroVariant"
        />

        <Grid variant="detail">
          <PageContent
            v-if="props.data?.content"
            :content="props.data.content"
            m2a-field="articles_content"
            :start-index="0"
          />

          <PageAuthorBlock :authors="props.data?.authors" :biographies="props.data?.biographies" />
        </Grid>
        <Transition name="reveal" mode="out-in" appear>
          <PageArticleFooter
            v-if="!isPageType"
            :type="state.type"
            :dossiers="state.dossiers"
            :themes="state.themes"
            :tags="state.tags"
            :forum-topic="props.data?.forumTopic"
          />
        </Transition>
      </div>
    </component>
  </component>
</template>

<script setup lang="ts">
import useIsChildPageOf from '~/composables/useIsChildPageOf';
import IntersectionObserver from '../IntersectionObserver.vue';

import type { TArticle } from '~~/types/local_types';
import type {
  ICmsArticlesTags,
  ICmsArticlesThemes,
  ICmsDossiersArticles,
} from '~~/types/_cms-types';
import { Transition } from 'vue';

const rootMarginRef = ref<number | null>(720 * 0.15);

const computedIntersectionOptions = computed(() => {
  return {
    rootMargin: `0px 0px ${rootMarginRef.value}px 0px`,
    threshold: 0,
  };
});

const componentRef = ref<HTMLElement | undefined>(undefined);
interface IProps {
  data?: TArticle;
  _index: number; // this is used to translate (from bottom to top) the entire page on load more.
}

const props = withDefaults(defineProps<IProps>(), {
  data: undefined,
  _index: 0,
});

const intersected = ref<boolean>(props._index === 0);

interface IState {
  variant: string;
  type?: string | null | undefined;
  tags?: ICmsArticlesTags[];
  themes?: ICmsArticlesThemes[];
  dossiers?: ICmsDossiersArticles[] | [];
  showMeta?: boolean;
  showShare?: boolean;
  heroVariant?: 'narrow' | 'wide';
}

const isPageType = computed(() => {
  return props.data?.articleType === 'paginas' || props.data?.articleType === 'opiniepanel/paginas';
});

const state: IState = reactive({
  variant: useIsChildPageOf(),
  type: props.data?.articleType || null,
  tags: props.data?.tags || [],
  themes: props.data?.themes || [],
  dossiers: props.data?.dossiers || [],
  showMeta: !isPageType.value,
  showShare: !isPageType.value,
  heroVariant: isPageType.value ? 'narrow' : 'wide',
});

const onIntersect = ({ visible }) => {
  if (visible) {
    intersected.value = true;
  }
};

const updateRootMargin = () => {
  rootMarginRef.value = window.innerHeight * 0.15;
};

onMounted(() => {
  updateRootMargin();
  window.addEventListener('resize', updateRootMargin);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateRootMargin);
});
</script>

<style scoped lang="postcss">
.dynamic-article {
  overflow: hidden;
  transform-origin: top center;
  will-change: opacity, transform;
  transform: translateY(0);
  opacity: 1;
  transition: none;

  &.is--not-first {
    transition:
      opacity 0.5s linear,
      transform 0.75s ease-out;
  }

  &.is--hidden {
    opacity: 0;

    @media (--md-viewport) {
      transform: translateY(3.5vh);
    }
  }
}
</style>
